import { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ArrowLeft, Sparkles, ExternalLink, CheckCircle2, Calendar } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import { GlowOrb } from "@/components/common/GlowOrb";
import MetricsBlock from "@/components/case-study/MetricsBlock";
import { useCaseStudy, usePublishedCaseStudies } from "@/data/caseStudyRepo";
import { getPublishedCaseStudies } from "@/data/caseStudies";
import { trackViewOnce, trackClick } from "@/lib/analytics";
import { openEstimateWizard, buildConsultationHref, rememberCaseStudyView } from "@/lib/cta";

const SERVICE_LABELS = {
  "ai-development": "AI Development",
  "ai-agent-development": "AI Agent Development",
  "custom-software-development": "Custom Software Development",
  "saas-development": "SaaS Development",
  "crm-development": "CRM Development",
  "mobile-app-development": "Mobile App Development",
  "web-application-development": "Web Application Development",
  "edtech-development": "EdTech Development",
  "mvp-development": "MVP Development",
  "software-modernisation": "Software Modernisation",
};

const MARKET_LABELS = {
  uk: "United Kingdom",
  us: "United States",
  pk: "Pakistan",
  ae: "United Arab Emirates",
  sa: "Saudi Arabia",
  de: "Germany",
  europe: "Europe",
};

const INDUSTRY_LABELS = {
  education: "Education",
  construction: "Construction",
};

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const preview = search.get("preview") === "1";
  const { data: cs, loading } = useCaseStudy(slug, { preview });
  const isPublished = cs && (cs.status === "published" || preview);

  useEffect(() => {
    if (!isPublished || !cs) return;
    trackViewOnce("case_study_view", {
      caseStudySlug: cs.slug,
      caseStudyName: cs.projectName,
      industry: cs.industry || "unknown",
    });
    // Session-scoped assisted-conversion signal (first-party sessionStorage
    // only — no PII, no cookies, cleared when the tab session ends).
    rememberCaseStudyView(cs.slug);
  }, [isPublished, cs]);

  // Loading state — show skeleton while DB fetch resolves (code-managed
  // records resolve synchronously, so this only shows for admin-managed).
  if (loading && !cs) {
    return (
      <div className="relative min-h-screen pt-32 max-w-3xl mx-auto px-6 text-center" data-testid="case-study-loading">
        <Seo title="Loading…" description="" noindex />
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-muted rounded w-1/3 mx-auto"></div>
          <div className="h-10 bg-muted rounded w-2/3 mx-auto"></div>
          <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  // Not found (or draft without preview) — noindex + friendly not-found.
  if (!cs || !isPublished) {
    return (
      <div className="relative min-h-screen pt-32 max-w-3xl mx-auto px-6 text-center" data-testid="case-study-not-found">
        <Seo
          title="Case Study Not Found"
          description="The case study you're looking for is not available."
          noindex
        />
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Case Studies · 404</div>
        <h1 className="mt-4 font-heading font-black text-4xl md:text-5xl">We couldn&apos;t find that case study.</h1>
        <p className="mt-4 text-muted-foreground">It may have been unpublished or the URL is incorrect.</p>
        <Link to="/case-studies" className="mt-8 inline-flex items-center gap-2 rounded-full px-5 py-3 bg-primary text-primary-foreground text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to case studies
        </Link>
      </div>
    );
  }

  const openQuote = (location = "hero") => {
    trackClick("cta_clicked", {
      cta_type: "get_project_estimate",
      source_type: "case_study",
      cta_location: location,
      case_study_slug: cs.slug,
      service_slug: cs.services?.[0] || undefined,
    });
    openEstimateWizard({
      serviceSlug: cs.services?.[0] || null,
      caseStudySlug: cs.slug,
      caseStudyName: cs.projectName,
      marketSlug: cs.markets?.[0] || null,
      sourceType: "case_study",
      ctaLocation: location,
    });
  };
  const consultationHref = buildConsultationHref({
    serviceSlug: cs.services?.[0] || null,
    caseStudySlug: cs.slug,
    caseStudyName: cs.projectName,
    marketSlug: cs.markets?.[0] || null,
  });

  const related = (cs.relatedProjects || [])
    .map((s) => {
      // Code-managed first (so UniCRM ↔ TS.ai cross-link stays intact);
      // admin cross-links will be enriched by a dynamic fetch in a future iteration.
      const code = getPublishedCaseStudies().find((c) => c.slug === s);
      return code ? { ...code, source: "code" } : null;
    })
    .filter(Boolean);

  const breadcrumb = breadcrumbLd([
    { name: "Home", path: "/" },
    { name: "Case Studies", path: "/case-studies" },
    { name: cs.projectName, path: `/case-studies/${cs.slug}` },
  ]);

  const softwareLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: cs.projectName,
    description: cs.shortDescription,
    url: cs.externalUrl || `https://www.cloudlabweb.com/case-studies/${cs.slug}`,
    image: cs.seo?.ogImage || cs.heroImage,
    applicationCategory: "BusinessApplication",
    operatingSystem: cs.platform || "Web",
    publisher: { "@id": "https://www.cloudlabweb.com/#organization" },
  };

  return (
    <div className="relative" data-testid={`case-study-${cs.slug}`}>
      <Seo
        title={cs.seo?.title || `${cs.projectName} · Case Study`}
        description={cs.seo?.description || cs.shortDescription}
        image={cs.seo?.ogImage || cs.heroImage}
        type="article"
        jsonLd={[breadcrumb, softwareLd]}
      />
      <GlowOrb className="top-10 -left-20" color="primary" size={520} />
      <GlowOrb className="bottom-40 -right-20" color="secondary" size={520} />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 pt-24 md:pt-28">
        <nav aria-label="Breadcrumb" className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span className="mx-2 opacity-50">/</span>
          <Link to="/case-studies" className="hover:text-primary transition-colors">Case Studies</Link>
          <span className="mx-2 opacity-50">/</span>
          <span className="text-foreground">{cs.projectName}</span>
        </nav>
      </div>

      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-6 pt-8 pb-12">
        <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-10 lg:gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-primary">
              <Sparkles className="w-3.5 h-3.5" /> Case Study{cs.industry && ` · ${INDUSTRY_LABELS[cs.industry] || cs.industry}`}
            </div>
            <h1
              className="mt-4 font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
              data-testid={`case-study-${cs.slug}-h1`}
            >
              {cs.projectName}
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
              {cs.heroSubtitle || cs.shortDescription}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => openQuote("hero")}
                data-testid={`case-study-${cs.slug}-hero-cta`}
                className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]"
              >
                <span>Get Project Estimate</span>
                <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>
              <Link
                to={consultationHref}
                onClick={() =>
                  trackClick("cta_clicked", {
                    cta_type: "book_consultation",
                    source_type: "case_study",
                    cta_location: "hero",
                    case_study_slug: cs.slug,
                    service_slug: cs.services?.[0] || undefined,
                  })
                }
                data-testid={`case-study-${cs.slug}-hero-consultation`}
                className="rounded-full px-5 py-3 border border-border text-sm inline-flex items-center gap-2 hover:bg-foreground/5"
              >
                <Calendar className="w-4 h-4" />
                Book a 15-Minute Consultation
              </Link>
              {cs.externalUrl && (
                <a
                  href={cs.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full px-5 py-3 text-sm inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
                  data-testid={`case-study-${cs.slug}-external`}
                >
                  Visit product <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1, duration: 0.7 }}>
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-[0_45px_100px_-40px_rgba(0,0,0,0.5)]">
              <div className="relative aspect-[16/10] bg-muted/40">
                {cs.heroImage && (
                  <img src={cs.heroImage} alt={cs.projectName} className="absolute inset-0 w-full h-full object-cover" />
                )}
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SNAPSHOT */}
      <section className="relative max-w-7xl mx-auto px-6 py-8 md:py-12" data-testid={`case-study-${cs.slug}-snapshot`}>
        <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Snapshot</div>
          <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6">
            {cs.industry && (
              <SnapshotField label="Industry" value={INDUSTRY_LABELS[cs.industry] || cs.industry} />
            )}
            {cs.solutionType && <SnapshotField label="Solution type" value={cs.solutionType} />}
            {cs.platform && <SnapshotField label="Platform" value={cs.platform} />}
            {cs.services?.length > 0 && (
              <SnapshotField
                label="Services"
                value={cs.services.map((s) => SERVICE_LABELS[s] || s).join(" · ")}
              />
            )}
            {cs.markets?.length > 0 && (
              <SnapshotField
                label="Market"
                value={cs.markets.map((m) => MARKET_LABELS[m] || m).join(" · ")}
              />
            )}
            {cs.technologies?.length > 0 && (
              <SnapshotField label="Technologies" value={cs.technologies.slice(0, 6).join(" · ")} />
            )}
          </div>
        </div>
      </section>

      {/* CHALLENGE + SOLUTION */}
      {(cs.challenge || cs.solution) && (
        <section className="relative max-w-7xl mx-auto px-6 py-10 md:py-14">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {cs.challenge && (
              <div className="rounded-3xl border border-border bg-card p-7 md:p-8" data-testid={`case-study-${cs.slug}-challenge`}>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">The challenge</div>
                <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">The operational problem.</h2>
                <p className="mt-4 text-base leading-relaxed text-foreground/85 whitespace-pre-line">{cs.challenge}</p>
              </div>
            )}
            {cs.solution && (
              <div className="rounded-3xl border border-border bg-card p-7 md:p-8" data-testid={`case-study-${cs.slug}-solution`}>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">The solution</div>
                <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">What Cloud Lab built.</h2>
                <p className="mt-4 text-base leading-relaxed text-foreground/85 whitespace-pre-line">{cs.solution}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* KEY CAPABILITIES */}
      {cs.capabilities?.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 py-10 md:py-14" data-testid={`case-study-${cs.slug}-capabilities`}>
          <div className="max-w-2xl">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Key capabilities</div>
            <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.1]">
              What the system actually does.
            </h2>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {cs.capabilities.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
                className="rounded-2xl border border-border bg-card p-5 md:p-6"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div className="mt-4 font-heading font-bold text-base leading-tight">{c.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      {cs.workflow?.length > 0 && (
        <section className="relative max-w-6xl mx-auto px-6 py-10 md:py-14" data-testid={`case-study-${cs.slug}-workflow`}>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">How it works</div>
          <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.1]">The workflow.</h2>
          <ol className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {cs.workflow.map((w, i) => (
              <motion.li
                key={w.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="rounded-2xl border border-border bg-card p-5"
              >
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center font-heading font-black text-base bg-primary text-primary-foreground">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-4 font-heading font-bold text-base leading-tight">{w.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
              </motion.li>
            ))}
          </ol>
        </section>
      )}

      {/* MEASURABLE RESULTS — verified-only, driven by projectMetrics.js (code) or inline (admin) */}
      <MetricsBlock caseStudy={cs} />

      {/* TECHNICAL IMPLEMENTATION */}
      {cs.technologies?.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 py-10 md:py-14 bg-muted/30 border-y border-border/60" data-testid={`case-study-${cs.slug}-tech`}>
          <div className="max-w-3xl">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Technical implementation</div>
            <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.05]">The engineering stack.</h2>
            <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
              A single stack — chosen for reliability, iteration speed and the specific integrations {cs.projectName} needed. Every technology below is one
              Cloud Lab shipped in production.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-2.5">
            {cs.technologies.map((t) => (
              <span
                key={t}
                className="px-3.5 py-1.5 rounded-full bg-background border border-border text-sm font-medium hover:border-primary/50 hover:text-primary transition-colors"
              >
                {t}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* CLOUD LAB'S ROLE */}
      {cs.cloudLabRole?.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 py-10 md:py-14" data-testid={`case-study-${cs.slug}-role`}>
          <div className="rounded-3xl border border-border bg-card p-7 md:p-10">
            <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-8 md:gap-12 items-start">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Cloud Lab&apos;s role</div>
                <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">What we delivered.</h2>
                <p className="mt-3 text-sm text-muted-foreground max-w-md">
                  End-to-end engineering ownership on the areas below. We collaborate closely with product owners on scope, priorities and roadmap.
                </p>
              </div>
              <ul className="grid sm:grid-cols-2 gap-3">
                {cs.cloudLabRole.map((r) => (
                  <li key={r} className="flex items-start gap-3 p-3 rounded-xl bg-background border border-border">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm leading-relaxed">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* RELATED SERVICES */}
      {cs.services?.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 py-10 md:py-14" data-testid={`case-study-${cs.slug}-services`}>
          <div className="max-w-2xl">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Related services</div>
            <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl tracking-tight leading-tight">Explore the services behind {cs.projectName}.</h2>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cs.services.map((s) => (
              <Link
                key={s}
                to={`/services/${s}`}
                className="group rounded-2xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
                data-testid={`case-study-${cs.slug}-service-${s}`}
              >
                <div className="font-heading font-bold text-base leading-tight group-hover:text-primary transition-colors">
                  {SERVICE_LABELS[s] || s}
                </div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary">
                  Read more <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* RELATED CASE STUDIES */}
      {related.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 py-10 md:py-14" data-testid={`case-study-${cs.slug}-related`}>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">More evidence</div>
              <div className="mt-1 font-heading font-bold text-2xl md:text-3xl">Related case studies</div>
            </div>
            <Link to="/case-studies" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-1">
              All case studies <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/case-studies/${r.slug}`}
                className="group block rounded-3xl overflow-hidden border border-border bg-card hover:border-primary/40 transition-colors"
                data-testid={`case-study-${cs.slug}-related-${r.slug}`}
              >
                <div className="aspect-[16/9] overflow-hidden bg-muted/40">
                  {r.heroImage && (
                    <img src={r.heroImage} alt={r.projectName} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  )}
                </div>
                <div className="p-5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-primary">{INDUSTRY_LABELS[r.industry] || r.industry}</div>
                  <div className="mt-1.5 font-heading font-bold text-lg leading-tight">{r.projectName}</div>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.shortDescription}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="relative py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative rounded-[32px] overflow-hidden p-8 md:p-14 bg-foreground text-background shadow-2xl">
            <div aria-hidden="true" className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl pointer-events-none bg-primary" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Building something similar?</div>
                <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-tight">
                  Let&apos;s build your version of {cs.projectName}.
                </h2>
                <p className="mt-4 text-background/80 leading-relaxed max-w-lg">
                  Share a rough spec or just a rough idea — we&apos;ll come back within one business day with a realistic scope, timeline and price.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <button onClick={() => openQuote("final")} data-testid={`case-study-${cs.slug}-final-cta`}
                  className="group inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 text-sm font-medium bg-primary text-primary-foreground">
                  <span>Get Project Estimate</span>
                  <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
                <Link
                  to={consultationHref}
                  onClick={() =>
                    trackClick("cta_clicked", {
                      cta_type: "book_consultation",
                      source_type: "case_study",
                      cta_location: "final",
                      case_study_slug: cs.slug,
                      service_slug: cs.services?.[0] || undefined,
                    })
                  }
                  data-testid={`case-study-${cs.slug}-final-consultation`}
                  className="rounded-full px-5 py-3 border border-background/25 text-sm inline-flex items-center gap-2 hover:bg-background/5 text-background"
                >
                  <Calendar className="w-4 h-4" />
                  Book a 15-Minute Consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SnapshotField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm md:text-base font-medium leading-snug">{value}</div>
    </div>
  );
}

// Optional helper (not currently used) — keeps published list handy for
// consumers that might want to prefetch.
export function _allPublishedSlugs() {
  return getPublishedCaseStudies().map((c) => c.slug);
}
